/* eslint-disable import/extensions */
import { PAGES } from '../../constants/GLOBAL_VARIABLE.js'
import {
  STATUS, ERROR_CODE, ERROR_NAME, SERVER_ERROR_NAME,
} from './enum.js'
import { Feature } from '../common'

// 아크레이더스 - ATL 2.0 본인인증 필수 대상 클라이언트
const REQUIRED_ATL_20_CLIENT_IDS = [
  'c877589b-6d67-45ea-bab7-e610a7382193', // 아크레이더스 DEV
  '25eee40a-2f99-43f6-8956-aed22a677f8d', // 아크레이더스 TEST
  'ebb0148e-cc6b-4128-a1c5-5da16e40fc59', // 아크레이더스 LIVE
  '37f5a99f-1bd7-4c5b-94e1-0f5a7a293957', // 블리자드 TEST
  '768b30eb-8a33-4fc7-b92e-ff063a60a895', // 블리자드 LIVE
]

class OAuth extends Feature {
  constructor({ mainUrl, myinfoUrl, corsList }) {
    super({ namespace: 'iac', enumKey: 'IAC.OAUTH' })
    this.mainUrl = mainUrl || 'https://signin.nexon.com'
    this.myinfoUrl = myinfoUrl || 'https://myinfo.nexon.com'

    const {
      client_id: clientId,
      response_type: responseType,
      // redirect_uri: redirectUri,
      scope,
      state,
      error_code: errorCode,
      error_name: errorName,
      extra_response_type: extraResponseType,
      client_env: clientEnv,
    } = this.queryParamWithCookie

    const redirectUri = this.getRedirectUriByQueryParamWithCookie({ corsList })

    this.clientId = clientId
    this.responseType = responseType
    this.scope = scope
    this.state = state
    this.redirectUri = redirectUri
    this.errorCode = errorCode
    this.errorName = errorName
    // (옵셔널) /oauth/authorize 쿼리로 유입되는 값. 값이 있을 때만 각 API로 전달한다.
    this.extraResponseType = extraResponseType
    this.clientEnv = clientEnv
    // /iac/v4/oauth/authorize 응답의 extra_responses.krpc_authentication_code (성공/실패 무관하게 캡처)
    this.krpcAuthenticationCode = undefined

    // ATL 2.0 본인인증 안내 모달이 떠 있는 동안 후속 redirectForFail/Success를 무력화하기 위한 플래그
    // (선행 단계에서 큐잉된 navigate가 모달 표시 직후 실행되어 흐름을 끊는 race condition 방지)
    this._verifyPromptInProgress = false
  }

  checkRequiredParam() {
    // NOTE : 기존 로직 영향성이 없도록 하기위해 신규 메소드에만 적용, 검증 후 기존 메소드에도 적용 검토
    if (!this.clientId || !this.responseType || !this.redirectUri || !this.scope) {
      console.error(
        'Please check your request query params.',
        `\nclient_id: ${this.clientId}\nresponse_type: ${this.responseType}\nredirect_uri: ${this.redirectUri}\nscope: ${this.scope}`,
      )
      return { status: STATUS.FAIL, errorCode: ERROR_CODE.INVALID_REQUEST }
    }
    return null
  }

  /**
   * Authorization Code 발급 요청
   * @return {object} { code: number, data: object, response: Response, errorData: object }
   */
  async getAuthorizationCode() {
    try {
      if (!this.clientId || !this.responseType || !this.redirectUri || !this.scope) {
        console.error(
          'Please check your request query params.',
          `\nclient_id: ${this.clientId}\nresponse_type: ${this.responseType}\nredirect_uri: ${this.redirectUri}\nscope: ${this.scope}`,
        )
        return { status: STATUS.FAIL, errorCode: ERROR_CODE.INVALID_REQUEST }
      }

      const params = new URLSearchParams({
        client_id: this.clientId,
        response_type: this.responseType,
        redirect_uri: this.redirectUri,
        scope: this.scope || '',
        lang: window.$i18next.language,
      })

      // (옵셔널) 값이 있을 때만 추가하여 기존 요청 형태를 유지한다.
      if (this.extraResponseType) {
        params.append('extra_response_type', this.extraResponseType)
      }

      const url = `${this.mainUrl}/iac/v4/oauth/authorize?${params.toString()}`

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include', // 쿠키 전송(webToken)
      })

      if (!response.ok) {
        throw response
      }

      const data = await response.json()
      // (옵셔널) 응답에 포함된 경우 리다이렉트 시 전달하기 위해 보관
      this.captureKrpcAuthenticationCode(data)
      if (data.code) {
        return { status: STATUS.OK, code: data.code }
      }

      return { status: STATUS.FAIL, errorCode: ERROR_CODE.INVALID_RESPONSE }
    } catch (e) {
      const errorData = typeof e.json === 'function' ? await e.json() : e
      // 실패 응답에도 extra_responses 가 포함될 수 있으므로 동일하게 캡처
      this.captureKrpcAuthenticationCode(errorData)
      console.error(e)

      if (e.status === 403 && errorData?.error?.name === SERVER_ERROR_NAME.NOT_FOUND_USER_CONSENT) {
        // 동의/허가 필요
        const property = errorData?.error?.property || {}
        return { status: STATUS.CONSENT, property }
      }

      if (
        e.status === 403
        && errorData?.error?.name === SERVER_ERROR_NAME.UNVERIFIED_ACCOUNT_NOT_SUPPORTED
        && REQUIRED_ATL_20_CLIENT_IDS.some((id) => id === String(this.clientId))
      ) {
        // ATL 1.0 미지원 계정 - ATL 2.0 본인인증 필요
        return { status: STATUS.ATL1_VERIFY_REQUIRED }
      }

      if (errorData?.error?.name) {
        return {
          status: STATUS.FAIL,
          errorCode: ERROR_CODE.FAIL_TO_GET_AUTHORIZATION_CODE,
          errorName: errorData?.error?.name,
        } // iac_error
      }

      return { status: STATUS.FAIL, errorCode: ERROR_CODE.FAIL_TO_GET_AUTHORIZATION_CODE } // server_error
    }
  }

  // OAuth 접근시 등록된 Client인지 검증
  async checkVerifyClient(platformType) {
    try {
      this.checkRequiredParam()

      const url = `${this.mainUrl}/iac/v1/oauth/verify/client`
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // 쿠키 전송(webToken)
        mode: 'cors',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: this.clientId,
          redirect_uri: this.redirectUri,
          platform_type: platformType.toUpperCase(),
          // (옵셔널) 값이 있을 때만 포함하여 기존 요청 형태를 유지한다.
          ...(this.clientEnv ? { client_env: this.clientEnv } : {}),
        }),
      })

      const data = await response.json()
      if (!response.ok) {
        this.redirectForFail(ERROR_CODE.IAC_INVALID_PLATFORM_TYPE, data?.error?.name)
        // redirect 큐잉 이후 검증 흐름을 계속 진행하면 후속 단계에서 추가 redirect/모달이 충돌할 수 있어 즉시 종료
        return { status: STATUS.FAIL, errorCode: ERROR_CODE.INVALID_RESPONSE }
      }

      if (data?.verified_client) {
        return { status: STATUS.OK, accessCode: data?.krpc_member_access_code }
      }
      this.redirectForFail(ERROR_CODE.IAC_INVALID_PLATFORM_TYPE, data?.error?.name)
      return { status: STATUS.FAIL, errorCode: ERROR_CODE.INVALID_RESPONSE }
    } catch (e) {
      console.error(e)
      this.redirectForFail(ERROR_CODE.IAC_UNKNOWN_ERROR, ERROR_NAME['113307'])
      return { status: STATUS.FAIL, errorCode: ERROR_CODE.INVALID_RESPONSE }
    }
  }

  hasError() {
    // 유저 동의 페이지에서 넘어온 에러가 있는지
    if (this.errorCode || this.errorName) {
      this.redirectForFail(this.errorCode, this.errorName)
    }

    return !!(this.errorCode || this.errorName)
  }

  async processAuthorization() {
    // authorization code 발급 요청
    const result = await this.getAuthorizationCode()

    switch (result.status) {
      case STATUS.OK:
        this.redirectForSuccess(result.code)
        break
      case STATUS.CONSENT:
        const cookieData = {
          client_id: this.clientId,
          response_type: this.responseType,
          scope: this.scope,
          state: this.state,
          redirect_uri: this.redirectUri ? encodeURIComponent(this.redirectUri) : '',
          // NOTE: 250529 property 속성에 할당된 값이 커질 경우, 쿠키 제한 사이즈를 초과하여 비정상 동작하는 이슈가 있어 주석 처리합니다.
          // ...result.property,
          signout_redirect_uri: encodeURIComponent(window.location.href),
        }
        this.namespaceCookie.setCookie(cookieData)
        this.gotoConsentPage()
        break
      case STATUS.ATL1_VERIFY_REQUIRED:
        await this.promptATL20VerifyAndRedirect()
        break
      case STATUS.FAIL:
        this.redirectForFail(result.errorCode, result.errorName)
        break
      default:
    }
  }

  /**
   * goto 정보제공동의 페이지
   */
  gotoConsentPage() {
    this.navigateToUrlWithoutHistory(`${this.mainUrl}/${PAGES.IAC_CONSENT_OAUTH}`)
  }

  /**
   * ATL 2.0 본인인증 안내 팝업 후 KRPC 내정보 본인인증 페이지로 이동
   */
  async promptATL20VerifyAndRedirect() {
    this._verifyPromptInProgress = true
    await window.$globalModal.warning({
      message: '본인인증이 필요합니다.\n지금 본인인증을 하시겠습니까?',
      confirmText: '확인',
    })

    const baseUrl = this.myinfoUrl.replace(/\/$/, '')
    const redirect = encodeURIComponent(window.location.href)
    this.navigateToUrlWithoutHistory(`${baseUrl}/verify?redirect=${redirect}`)
  }

  redirectForSuccess(code) {
    if (this._verifyPromptInProgress) return
    const params = new URLSearchParams({ code })
    this.redirectWithParams(params)
  }

  redirectForFail(errorCode, errorName) {
    if (this._verifyPromptInProgress) return
    const params = new URLSearchParams({
      error_code: errorCode,
      error_name: errorName || ERROR_NAME[errorCode],
    })

    this.redirectWithParams(params)
  }

  redirectWithParams(params) {
    if (this.state) {
      params.append('state', this.state)
    }

    // (옵셔널) /iac/v4/oauth/authorize 응답에 krpc_authentication_code 가 있었던 경우,
    // 성공/실패 모든 리다이렉트 경로에서 동일하게 전달한다. (없으면 기존과 동일)
    if (this.krpcAuthenticationCode) {
      params.append('krpc_authentication_code', this.krpcAuthenticationCode)
    }

    super.redirectWithParams(this.redirectUri, params)
  }

  /**
   * /iac/v4/oauth/authorize 응답에서 extra_responses.krpc_authentication_code 를 보관한다.
   * @param {object} responseBody 성공/실패 응답 body
   */
  captureKrpcAuthenticationCode(responseBody) {
    const code = responseBody?.extra_responses?.krpc_authentication_code
    if (code) {
      this.krpcAuthenticationCode = code
    }
  }
}

export default OAuth
